import cn from 'classnames';
import dayjs from 'dayjs';
import TZ from 'dayjs/plugin/timezone';
import UTC from 'dayjs/plugin/utc';
import Image from 'next/image';
import { FC, Fragment, useEffect, useState } from 'react';
import useSWR, { preload } from 'swr';

import Selector from '~components/common/Selector';
import Loading from '~components/ui/Loading';
import Poster from '~components/ui/Poster';
import { getLinearGradient } from '~components/ui/ShadowImage';
import { getFromAPI } from '~utils/api/common';
import go from '~utils/go';
import useEffectOnUpdate from '~utils/hooks/useEffectOnUpdate';

import { Attachment, Show, ShowWithMovie } from '~types';

import styles from './ShowHeader.module.css';

dayjs.extend(UTC);
dayjs.extend(TZ);

const ROOM_TYPE = {
  default: 'Sala normal',
  mega: 'Mega Pantalla',
  comfort: 'Confort',
  premiere: 'Premiere',
};

interface Props {
  dates: string[];
  show: ShowWithMovie;
  schedules: Show[];
}

const ShowSchedules: FC<Props> = ({ show, dates, schedules }) => {
  const [selectedDate, setSelectedDate] = useState(dayjs(show.schedule).format());
  const { MovieId, CinemaId } = show;
  const date = dayjs(selectedDate).startOf('day').format();
  const { data: shows, isLoading } = useSWR<Show[]>(
    selectedDate !== dayjs(show.schedule).format()
      ? `/shows/movie/${MovieId}?CinemaId=${CinemaId}&date=${date}`
      : null,
    { revalidateOnFocus: false },
  );
  const movie = show.Movie;
  const tz = show.Cinema?.timezone || 'America/Mazatlan';
  const getHref = (sessionId: number) => `/comprar-boletos/${movie.slug}/${sessionId}?step=inicio`;
  const showsKey = shows?.map((item) => item.sessionId).join(',');

  useEffectOnUpdate(() => {
    if (shows?.length) {
      const selectedTime = dayjs(show.schedule).format('HH:mm');
      const sessionWithSameHour = shows.find(
        (s) => selectedTime === dayjs(s.schedule).format('HH:mm'),
      );
      if (sessionWithSameHour) {
        preload(`/shows/seats/${sessionWithSameHour.sessionId}`, (endpoint) =>
          getFromAPI(endpoint, { notifyErrors: false }),
        );
        go(getHref(sessionWithSameHour.sessionId), { prefetch: true });
      } else {
        const firstSession = shows[0];
        preload(`/shows/seats/${firstSession.sessionId}`, (endpoint) =>
          getFromAPI(endpoint, { notifyErrors: false }),
        );
        go(getHref(firstSession.sessionId), { prefetch: true });
      }
    }
  }, [showsKey]);

  useEffect(() => {
    if (document !== undefined) {
      document?.getElementById('selectedDay')?.scrollIntoView();
      document?.getElementById('selectedSchedule')?.scrollIntoView();
    }
  }, []);

  return (
    <Loading show={isLoading} theme="dark" size="large" label="Cambiando día">
      <header className={styles.root}>
        <Image
          src={`${movie?.banner?.url}`}
          alt={`Imagen promocional de la película ${movie.title}`}
          fill
          className="opacity-50 object-cover"
          style={
            movie?.banner?.dominantColors
              ? { backgroundImage: getLinearGradient(movie?.banner?.dominantColors) }
              : {}
          }
        />
        <div className={styles.shadow} />
        <div className={styles.body}>
          <div className={styles.gridContainer}>
            <div className="md:row-span-3">
              <span className={styles.thumbnail}>
                <Poster data={movie.poster as Attachment} />
              </span>
            </div>
            <div className="col-span-5 md:col-span-5">
              <h1 className={styles.title}>{movie.title}</h1>
              <p className={styles.subtitle}>{movie.originalTitle}</p>
            </div>
            <div className="col-span-6 self-end md:col-span-2">
              <p className={styles.toolsTitle}>Fecha</p>
              <Selector
                data={dates}
                keyExtractor={(item) => item}
                onClick={setSelectedDate}
                renderItem={(item) => {
                  const day = dayjs(item).tz(tz).format('ddd');
                  const month = dayjs(item).tz(tz).format('MMM DD');

                  return (
                    <div
                      className={cn(styles.scheduleItem, {
                        [styles.selectedItem]: dayjs(selectedDate).isSame(item, 'day'),
                      })}
                      onMouseEnter={() => {
                        const newDate = dayjs(item).tz(tz).format();
                        preload(
                          `/shows/movie/${MovieId}?CinemaId=${CinemaId}&date=${newDate}`,
                          getFromAPI,
                        );
                      }}
                      {...(dayjs(selectedDate).isSame(item, 'day') ? { id: 'selectedDay' } : {})}
                    >
                      <span className="block text-xs">
                        {`${month.charAt(0).toUpperCase()}${month.slice(1)}`}
                      </span>
                      <span className="block text-white">
                        {`${day.charAt(0).toUpperCase()}${day.slice(1)}`}
                      </span>
                    </div>
                  );
                }}
              />
            </div>
            <div className="col-span-6 self-end md:col-span-3">
              {schedules.length < 1 ? (
                <Fragment>
                  <p className={styles.toolsTitle}>Horarios</p>
                  <p>No hay horarios para este día.</p>
                </Fragment>
              ) : (
                <Fragment>
                  <p className={styles.toolsTitle}>Horarios</p>
                  <Selector
                    data={schedules.filter((schedule) =>
                      dayjs(schedule.schedule)
                        .tz(tz)
                        .isAfter(dayjs().tz(tz).subtract(20, 'minutes')),
                    )}
                    keyExtractor={(item) => item.id.toString()}
                    renderItem={(item) => {
                      const room =
                        Object.keys(item.roomType).find((room) => {
                          // @ts-ignore
                          return item.roomType[room] === true;
                        }) ?? 'default';

                      return (
                        <a
                          href={getHref(item.sessionId)}
                          onMouseEnter={() => {
                            preload(`/shows/seats/${item.sessionId}`, (endpoint) =>
                              getFromAPI(endpoint, { notifyErrors: false }),
                            );
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            go(getHref(item.sessionId), { scroll: false });
                          }}
                          className={cn(styles.scheduleItem, {
                            [styles.premiereRoom]: item.roomType.premiere === true,
                            [styles.comfortRoom]: item.roomType.comfort === true,
                            [styles.selectedItem]: item.sessionId === show?.sessionId,
                          })}
                          // @ts-ignore
                          title={ROOM_TYPE[room]}
                          {...(item.sessionId === show?.sessionId
                            ? { id: 'selectedSchedule' }
                            : {})}
                        >
                          {item?.Projection && (
                            <span className="block text-xs">
                              {`${item.Projection.format}/${item.Projection.lang}`}
                            </span>
                          )}
                          <span className="block">
                            <time dateTime={dayjs(item.rawSchedule, 'YYYY-MM-DD HH:mm').format()}>
                              {dayjs(item.rawSchedule, 'YYYY-MM-DD HH:mm').format('hh:mm' + ' a')}
                            </time>
                          </span>
                        </a>
                      );
                    }}
                  />
                </Fragment>
              )}
            </div>
          </div>
        </div>
      </header>
    </Loading>
  );
};

export default ShowSchedules;
